import { AxiosRequestConfig } from "axios";
import { CohesiveAPIClient } from "../../client";
import { route } from "../../utils";
import {
  CreateFlowScheduleRequest,
  DuplicateFlowScheduleRequest,
  FlowSchedule,
  FlowScheduleResponse,
  FlowScheduleRun,
  FlowSchedulesResponse,
  ListFlowScheduleRunsParams,
  ListFlowScheduleRunsResponse,
  ListFlowSchedulesParams,
  RunFlowScheduleResponse,
  UpdateFlowScheduleRequest,
} from "./types";

export * from "./types";

const ENDPOINTS = {
  LIST: "/api/v2/org/:organization_id/user/:user_id/flow-schedules",
  CREATE: "/api/v2/org/:organization_id/user/:user_id/flow-schedules",
  SHOW: "/api/v2/org/:organization_id/user/:user_id/flow-schedules/:flow_schedule_id",
  UPDATE:
    "/api/v2/org/:organization_id/user/:user_id/flow-schedules/:flow_schedule_id",
  DELETE:
    "/api/v2/org/:organization_id/user/:user_id/flow-schedules/:flow_schedule_id",
  DUPLICATE:
    "/api/v2/org/:organization_id/user/:user_id/flow-schedules/:flow_schedule_id/duplicate",
  RUNS: "/api/v2/org/:organization_id/user/:user_id/flow-schedules/:flow_schedule_id/runs",
  RUN: "/api/v2/org/:organization_id/user/:user_id/flow-schedules/:flow_schedule_id/run",
};

export class FlowSchedulesAPI {
  constructor(private client: CohesiveAPIClient) {}

  /** Unfiltered = the caller's own schedules; canvas_id/workspace_id list all owners' there. */
  async list(
    params?: ListFlowSchedulesParams,
    config?: AxiosRequestConfig,
  ): Promise<FlowSchedule[]> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();
    const response = await this.client.axios.get<FlowSchedulesResponse>(
      route(ENDPOINTS.LIST, {
        organization_id: organizationId,
        user_id: userId,
      }),
      { ...config, params: { ...config?.params, ...params } },
    );
    return response.data.data.flow_schedules;
  }

  async getById(
    id: string,
    config?: AxiosRequestConfig,
  ): Promise<FlowSchedule> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();
    const response = await this.client.axios.get<FlowScheduleResponse>(
      route(ENDPOINTS.SHOW, {
        organization_id: organizationId,
        user_id: userId,
        flow_schedule_id: id,
      }),
      config,
    );
    return response.data.data.flow_schedule;
  }

  async create(
    data: CreateFlowScheduleRequest,
    config?: AxiosRequestConfig,
  ): Promise<FlowSchedule> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();
    const response = await this.client.axios.post<FlowScheduleResponse>(
      route(ENDPOINTS.CREATE, {
        organization_id: organizationId,
        user_id: userId,
      }),
      data,
      config,
    );
    return response.data.data.flow_schedule;
  }

  async update(
    id: string,
    data: UpdateFlowScheduleRequest,
    config?: AxiosRequestConfig,
  ): Promise<FlowSchedule> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();
    const response = await this.client.axios.patch<FlowScheduleResponse>(
      route(ENDPOINTS.UPDATE, {
        organization_id: organizationId,
        user_id: userId,
        flow_schedule_id: id,
      }),
      data,
      config,
    );
    return response.data.data.flow_schedule;
  }

  /** Hard delete (no trash). Idempotent server-side. */
  async delete(id: string, config?: AxiosRequestConfig): Promise<void> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();
    await this.client.axios.delete(
      route(ENDPOINTS.DELETE, {
        organization_id: organizationId,
        user_id: userId,
        flow_schedule_id: id,
      }),
      config,
    );
  }

  /** The copy is created paused (next_run_at cleared) but inherits the cron. */
  async duplicate(
    id: string,
    data: DuplicateFlowScheduleRequest,
    config?: AxiosRequestConfig,
  ): Promise<FlowSchedule> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();
    const response = await this.client.axios.post<FlowScheduleResponse>(
      route(ENDPOINTS.DUPLICATE, {
        organization_id: organizationId,
        user_id: userId,
        flow_schedule_id: id,
      }),
      data,
      config,
    );
    return response.data.data.flow_schedule;
  }

  /** Returns the full envelope so callers can page on pagination.next_ts. */
  async listRuns(
    id: string,
    params?: ListFlowScheduleRunsParams,
    config?: AxiosRequestConfig,
  ): Promise<ListFlowScheduleRunsResponse> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();
    const response = await this.client.axios.get<ListFlowScheduleRunsResponse>(
      route(ENDPOINTS.RUNS, {
        organization_id: organizationId,
        user_id: userId,
        flow_schedule_id: id,
      }),
      { ...config, params: { ...config?.params, ...params } },
    );
    return response.data;
  }

  /** Starts a manual run immediately (202). Does not advance the schedule's cadence. */
  async runNow(
    id: string,
    config?: AxiosRequestConfig,
  ): Promise<FlowScheduleRun> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();
    const response = await this.client.axios.post<RunFlowScheduleResponse>(
      route(ENDPOINTS.RUN, {
        organization_id: organizationId,
        user_id: userId,
        flow_schedule_id: id,
      }),
      {},
      config,
    );
    return response.data.data.run;
  }
}
